import { Amplify, type ResourcesConfig } from 'aws-amplify';
import type { TenantSignInConfig } from '@/api/iam';

/**
 * Bootstraps Amplify v6 with a per-tenant Cognito config. Called once after
 * the org's sign-in config has been fetched (or with the env-var fallback
 * for non-multi-tenant local dev).
 */
export function configureAmplify(config: TenantSignInConfig | null): void {
  const resolved = resolveCognitoConfig(config);
  if (!resolved) return;
  // Amplify's Cognito type narrows the user-pool-only and identity-pool-only
  // shapes into an intersection in TS; the runtime accepts either. Cast.
  Amplify.configure({ Auth: { Cognito: resolved } } as ResourcesConfig);
}

function resolveCognitoConfig(config: TenantSignInConfig | null) {
  if (config) {
    return config.identityPoolId
      ? {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolClientId,
          identityPoolId: config.identityPoolId,
        }
      : { userPoolId: config.userPoolId, userPoolClientId: config.userPoolClientId };
  }
  // Fallback — env-var Cognito for local dev.
  const userPoolId = import.meta.env.VITE_AMPLIFY_USER_POOL_ID;
  const userPoolClientId = import.meta.env.VITE_AMPLIFY_USER_POOL_CLIENT_ID;
  const identityPoolId = import.meta.env.VITE_AMPLIFY_IDENTITY_POOL_ID;
  if (!userPoolId || !userPoolClientId) return null;
  return identityPoolId
    ? { userPoolId, userPoolClientId, identityPoolId }
    : { userPoolId, userPoolClientId };
}
